/**
 * LifeEntity - State machine for a single life
 * 
 * Manages 4 zones: Home, Auto, Health, Liability
 * Tracks stability, recovery, and cost accumulation
 * Responds to behavioral choices and events
 */

export class LifeEntity {
  constructor(id, config = {}) {
    this.id = id;
    this.time = 0;
    
    // 4 ZONES - Each has condition, exposure, and recovery capacity
    this.zones = {
      home: {
        condition: 100,           // 0-100, degraded by events
        weatherExposure: 0.5,     // 0-1, affected by location
        repairCapacity: 1.0,      // Recovery speed multiplier
        maintenanceLevel: 0.7,    // User behavior (0=deferred, 1=proactive)
        coverage: 0,              // Protection level (0-1)
        costAccumulation: 0
      },
      auto: {
        condition: 100,
        trafficDensity: 0.5,      // Environmental factor
        drivingStyle: 0.5,        // 0=defensive, 1=aggressive
        collisionProbability: 0.1,
        coverage: 0,
        costAccumulation: 0
      },
      health: {
        condition: 100,
        baselineWellness: 0.7,    // Constitution
        preventativeCare: 0.5,    // User behavior
        recoverySpeed: 1.0,
        coverage: 0,
        costAccumulation: 0
      },
      liability: {
        condition: 100,
        proximityToOthers: 0.5,   // Living density
        legalExposure: 0.3,
        socialRisk: 0.2,
        coverage: 0,
        costAccumulation: 0
      }
    };

    // SHARED METRICS
    this.stabilityMeter = 100;    // 0-100, primary health indicator
    this.recoveryBandwidth = 1.0; // Multiplier for all recoveries
    this.totalCostAccumulation = 0;
    
    // LIFESTYLE FACTORS (long-tail effects)
    this.lifestyle = {
      commuteLength: 0.5,         // 0=short, 1=long
      livingDensity: 0.5,         // 0=rural, 1=urban
      workStress: 0.5,            // 0=low, 1=high
      environmentExposure: 0.5    // 0=protected, 1=exposed
    };

    // STATE
    this.activeEvents = [];        // Currently affecting this life
    this.eventHistory = [];        // Past events for analysis
    this.isRecovering = false;
    this.isPaused = false;
    
    // VISUAL STATE (for rendering)
    this.visualState = {
      position: { x: 0, y: 0, z: 0 },
      animations: [],
      particles: []
    };
  }

  /**
   * Main update loop - called every frame/tick
   */
  update(deltaTime) {
    if (this.isPaused) return;
    
    this.time += deltaTime;
    
    // Update each zone
    this.updateZones(deltaTime);
    
    // Process active events
    this.processEvents(deltaTime);
    
    // Calculate stability
    this.updateStability();
    
    // Passive degradation (inaction is still a choice)
    this.applyPassiveDegradation(deltaTime);
    
    // Recovery processes
    this.applyRecovery(deltaTime);
    
    return this.getState();
  }

  updateZones(deltaTime) {
    // Each zone degrades slightly based on behavior and environment
    Object.keys(this.zones).forEach(zoneKey => {
      const zone = this.zones[zoneKey];
      
      // Behavioral impact on degradation rate
      let degradationRate = 0.01; // Base rate per second
      
      if (zoneKey === 'home') {
        degradationRate *= (1 - zone.maintenanceLevel);
        degradationRate *= (1 + zone.weatherExposure);
      } else if (zoneKey === 'auto') {
        degradationRate *= (0.5 + zone.drivingStyle);
        degradationRate *= (1 + zone.trafficDensity * 0.5);
      } else if (zoneKey === 'health') {
        degradationRate *= (1 - zone.preventativeCare);
        degradationRate *= (1 + this.lifestyle.workStress * 0.3);
      }
      
      // Apply degradation (clamp at 0)
      zone.condition = Math.max(0, zone.condition - degradationRate * deltaTime);
      
      // Cost accumulation based on coverage
      zone.costAccumulation += zone.coverage * 0.1 * deltaTime;
    });
  }

  processEvents(deltaTime) {
    this.activeEvents = this.activeEvents.filter(event => {
      event.duration -= deltaTime;
      
      if (event.duration <= 0) {
        // Event complete - apply final damage
        this.applyEventDamage(event);
        this.eventHistory.push(event);
        return false; // Remove from active events
      }
      
      return true; // Keep active
    });
  }

  applyEventDamage(event) {
    const zone = this.zones[event.zone];
    if (!zone) return;
    
    // 3-LAYER EVENT DAMAGE
    // Layer 1: Systemic force (unavoidable core damage)
    const systemicDamage = event.baseDamage * 0.4;
    
    // Layer 2: Collective amplification (partially mitigable)
    const collectiveDamage = event.baseDamage * 0.3 * (1 - this.recoveryBandwidth * 0.5);
    
    // Layer 3: Personal exposure (user choices matter here)
    let personalDamage = event.baseDamage * 0.3;
    
    // Behavioral mitigation
    if (event.zone === 'auto') {
      personalDamage *= (1 - (1 - zone.drivingStyle) * 0.5); // Defensive driving helps
    } else if (event.zone === 'home') {
      personalDamage *= (1 - zone.maintenanceLevel * 0.6);
    } else if (event.zone === 'health') {
      personalDamage *= (1 - zone.preventativeCare * 0.4);
    }
    
    // Coverage mitigation (absorbs shock)
    const coverageAbsorption = zone.coverage * 0.7;
    personalDamage *= (1 - coverageAbsorption);
    
    // Total damage
    const totalDamage = systemicDamage + collectiveDamage + personalDamage;
    
    // Apply to zone
    zone.condition = Math.max(0, zone.condition - totalDamage);
    
    // Stability hit
    this.stabilityMeter = Math.max(0, this.stabilityMeter - totalDamage * 0.5);
    
    // Store mitigation info for feedback
    event.mitigationBreakdown = {
      systemicDamage,
      collectiveDamage,
      personalDamage,
      absorbed: event.baseDamage - totalDamage
    };
  }

  updateStability() {
    // Stability is weighted average of all zones
    const zoneConditions = Object.values(this.zones).map(z => z.condition);
    const avgCondition = zoneConditions.reduce((a, b) => a + b, 0) / zoneConditions.length;
    
    // Stability gradually moves toward average condition
    const targetStability = avgCondition;
    this.stabilityMeter += (targetStability - this.stabilityMeter) * 0.1;
    
    // Update recovery bandwidth based on stability
    this.recoveryBandwidth = 0.5 + (this.stabilityMeter / 100) * 0.5; // 0.5 to 1.0
  }

  applyPassiveDegradation(deltaTime) {
    // Inaction causes slow system drift
    const driftRate = 0.005 * deltaTime;
    this.stabilityMeter = Math.max(0, this.stabilityMeter - driftRate);
  }

  applyRecovery(deltaTime) {
    // Each zone slowly recovers based on recovery bandwidth
    Object.values(this.zones).forEach(zone => {
      if (zone.condition < 100) {
        const recoveryRate = zone.repairCapacity * this.recoveryBandwidth * 2; // 2% per second at full capacity
        zone.condition = Math.min(100, zone.condition + recoveryRate * deltaTime);
      }
    });
  }

  // EXTERNAL API - User actions

  setBehavior(zoneKey, parameter, value) {
    if (!this.zones[zoneKey]) return;
    
    this.zones[zoneKey][parameter] = Math.max(0, Math.min(1, value));
  }

  setCoverage(zoneKey, level) {
    if (!this.zones[zoneKey]) return;
    
    this.zones[zoneKey].coverage = Math.max(0, Math.min(1, level));
  }

  setLifestyle(parameter, value) {
    if (this.lifestyle[parameter] !== undefined) {
      this.lifestyle[parameter] = Math.max(0, Math.min(1, value));
    }
  }

  receiveEvent(event) {
    this.activeEvents.push({
      ...event,
      startTime: this.time,
      duration: event.duration || 2.0 // Default 2 second event
    });
  }

  pause() {
    this.isPaused = true;
  }

  resume() {
    this.isPaused = false;
  }

  getState() {
    return {
      id: this.id,
      time: this.time,
      zones: this.zones,
      stabilityMeter: this.stabilityMeter,
      recoveryBandwidth: this.recoveryBandwidth,
      totalCostAccumulation: this.totalCostAccumulation,
      lifestyle: this.lifestyle,
      activeEvents: this.activeEvents,
      isPaused: this.isPaused,
      visualState: this.visualState
    };
  }
}

